import { env } from "@/config/env"
import { HttpClient } from "@/shared/lib/http-client"
import type { AuraScanReport } from "@/features/aura-scan-report/types"
import type { SoulmateReport } from "@/features/soulmate-report"

import { isReportId, type ReportId } from "../config/reports-data"

/** Structured payload on native in-app reports (`ready` only). */
export type NativeReportContent = SoulmateReport | AuraScanReport

/**
 * Report lifecycle from the backend:
 *  - `purchasable` — not bought yet (buy with `planId` at `price`);
 *  - `processing`  — bought, generating (see `estimatedReadyAt`);
 *  - `ready`       — bought, available (`url` set);
 *  - `failed`      — generation failed.
 */
export type UserReportStatus = "purchasable" | "processing" | "ready" | "failed"

/**
 * One report for the signed-in user, as returned by `GET /v1/reports/me`. The
 * backend returns an item for EVERY report type. `url` is non-null ONLY when
 * `ready`; `estimatedReadyAt` is set ONLY when `processing`; `createdAt` is null
 * when never bought. `price`/`currency`/`planId` drive the purchase (webapp).
 */
export interface UserReport {
  type: ReportId
  status: UserReportStatus
  url: string | null
  createdAt: string | null
  /** ISO estimate of when a `processing` report will be ready (null otherwise). */
  estimatedReadyAt: string | null
  price: number
  currency: string
  /** Plan code the webapp charges to buy this report. */
  planId: string
  /**
   * Structured content of a native (in-app) report (soulmate / aura-scan).
   * Present ONLY when `ready` for those types; PDF reports expose `url` instead.
   * Aura Scan always has `url: null`. Lets the report page render from the list
   * with no extra fetch.
   */
  content?: NativeReportContent
}

/** Backend response envelope: `{ data }` on success (see user-service.ts). */
interface DataEnvelope<T> {
  data: T
}

/** Raw item shape before we narrow `type` to a known {@link ReportId}. */
interface RawUserReport {
  type: string
  status: string
  url: string | null
  createdAt: string | null
  estimatedReadyAt: string | null
  price: number
  currency: string
  planId: string
  content?: NativeReportContent | null
}

const REPORT_STATUSES: readonly UserReportStatus[] = [
  "purchasable",
  "processing",
  "ready",
  "failed",
]

function isReportStatus(value: unknown): value is UserReportStatus {
  return typeof value === "string" && REPORT_STATUSES.includes(value as UserReportStatus)
}

/** Keep only well-formed items whose `type` maps to a known report; drop the rest. */
function toUserReport(raw: RawUserReport): UserReport | null {
  if (!isReportId(raw.type) || !isReportStatus(raw.status)) return null
  return {
    type: raw.type,
    status: raw.status,
    url: raw.status === "ready" ? (raw.url ?? null) : null,
    createdAt: raw.createdAt ?? null,
    estimatedReadyAt: raw.status === "processing" ? (raw.estimatedReadyAt ?? null) : null,
    price: typeof raw.price === "number" ? raw.price : 0,
    currency: raw.currency || "USD",
    planId: raw.planId || "",
    ...(raw.status === "ready" && raw.content ? { content: raw.content } : {}),
  }
}

/**
 * Client for the authed reports list (`GET /v1/reports/me`). The backend derives
 * the owner from the Firebase token and returns every report's url/status/date.
 */
class UserReportsService extends HttpClient {
  async listForMe(token: string): Promise<UserReport[]> {
    const body = await this.get<DataEnvelope<RawUserReport[]>>("/v1/reports/me", {
      headers: { Authorization: `Bearer ${token}` },
    })
    const items = Array.isArray(body?.data) ? body.data : []
    return items.map(toUserReport).filter((report): report is UserReport => report !== null)
  }
}

const userReportsService = new UserReportsService({ baseURL: env.VITE_API_URL })

/**
 * Fetch the signed-in user's reports. Returns `[]` when the API base URL is not
 * configured (mirrors {@link import("./pdf-report-service").requestMyStarReport}).
 */
export async function getMyReports(token: string): Promise<UserReport[]> {
  if (!env.VITE_API_URL) return []
  return userReportsService.listForMe(token)
}
